import { put, takeLatest, all, call, select } from 'redux-saga/effects'
import { language } from 'context/LanguageContext/strings'

import { util } from 'utils'
import { request, getExcludedTags } from 'utils/requestModel'
import { isEmpty } from 'utils/appUtils'
import {
    actions,
    getHomePageItemsSuccess,
    getHomePageItemsFailedDone,
    filterErrors,
    getCategoryFiltersSuccess,
    applyCategoryFiltersSuccess,
    getSubCategoryFiltersSuccess,
    applySubCategoryFiltersSuccess,
    getProductDetailsByIdSuccess,
    getProductDetailsByIdFailed,
    getProductDetailsSuccess,
    getProductDetailsFailed,
    searchProductsFailed,
    searchProductsSuccess,
    setSearchedValue,
    getSearchedProductFailed,
    getSearchedProductSuccess,
    loadProductsSuccess,
    loadProductsFailed,
    setSelectedLocalizedSubCategory,
    getStoreAddressFailed,
    getStoreAddressSuccess,
    getHomePageCategoryList,
    getHomePageCategoryItemsSuccess,
    getHomePageCategoryItemsFailedDone,
    getHomePageItems as getHomePageData,
    applyCateringFiltersSuccess,
    getSubCateringFiltersSuccess,
    applySubCateringFiltersSuccess,
    getCateringFiltersSuccess,
} from './actions'
import { api } from '../../network/api'
import {
    homePageItemsURL,
    fetchSubCategories,
    getFiltersURL,
    productDetailsURL,
    productSearchUrl,
    fetchGlobalCategoriesUrl,
    storeProductSearchUrl,
    productDetailsByIdURL,
    getStoreAddressUrl,
    homePageCategoryURL,
    fetchCateringProducts,
    fetchGlobalCateringProductsUrl,
    getCateringFiltersURL,
} from './urls'
import * as queries from './queries'
import { getCateringData } from 'utils/cateringStorage'

/**
 * Set the home page data in session storage so subsequent calls will get from cache
 * @param {*} key
 * @param {*} data
 */
function cacheHomeData(key, data) {
    if (sessionStorage) {
        try {
            sessionStorage.setItem(key, data)
            // eslint-disable-next-line no-empty
        } catch (err) {}
    }
}
/**
 * Get the cached data from session storage
 * @param {*} key
 * @returns
 */
function getCachedHomeData(key) {
    if (sessionStorage) {
        return sessionStorage.getItem(key)
    }
    return null
}
/**
 * Generating dynamic cache key
 * @param {*} storeId
 * @returns
 */
function getKey(storeId, prefix) {
    prefix = isEmpty(prefix) ? 'home-data' : prefix
    return isEmpty(storeId) ? prefix : `${prefix}-${storeId}`
}

export function* getHomePageCategories() {
    try {
        let url = homePageCategoryURL
        const { products, location } = yield select()
        const storeLocation = location?.toJS()
        const storeDetails = storeLocation?.selectedStore
        const storeId = products?.storeId ? products.storeId : storeDetails?.store_id
        let key = getKey(null, 'home-category')
        if (storeId) {
            url = url.concat('&store_id=').concat(storeId).concat('&reco=on')
            const exclude_tags = getExcludedTags()
            if (exclude_tags.length) {
                url = url.concat('&exclude_tags=').concat(exclude_tags)
            }
            key = getKey(storeId, 'home-category')
        } else {
            url = url.concat('&reco=on')
        }
        let response = getCachedHomeData(key)
        if (!isEmpty(response)) {
            response = JSON.parse(response)
        } else {
            response = yield call(api, url, 'GET', 1, 0, null, false, language)
            if (!isEmpty(response)) {
                cacheHomeData(key, JSON.stringify(response))
            }
        }
        const homePageResponse = util.parseHomePageCategoryResponse({ response })
        yield put(getHomePageCategoryItemsSuccess(homePageResponse))
    } catch (error) {
        console.log(error)
        yield put(getHomePageCategoryItemsFailedDone())
    }
}
export function* getHomePageItems() {
    try {
        let url = homePageItemsURL
        const { location } = yield select()
        const storeLocation = location?.toJS()
        const storeDetails = storeLocation?.selectedStore
        const storeId = storeDetails?.store_id || storeDetails?.id
        let key = getKey()
        const timeStamp = new Date().getTime()
        if (storeId) {
            url = url
                .concat('&store_id=')
                .concat(storeId)
                .concat('&reco=on')
                .concat('&timeStamp=')
                .concat(timeStamp)
            const exclude_tags = getExcludedTags()
            if (exclude_tags.length) {
                url = url.concat('&exclude_tags=').concat(exclude_tags)
            }
            key = getKey(storeId)
        } else {
            url = url.concat('&reco=on')
        }
        let response = getCachedHomeData(key)
        if (!isEmpty(response)) {
            response = JSON.parse(response)
        } else {
            response = yield call(
                api,
                url,
                'GET',
                1,
                0,
                null,
                false,
                language,
                null,
                null,
                null,
                homePageItemsURL
            )
        }
        const homePageResponse = util.parseHomePageResponse({ response })
        yield put(getHomePageItemsSuccess(homePageResponse))
    } catch (error) {
        console.log(error)
        yield put(getHomePageItemsFailedDone())
    }
}

function* getStoreAddress() {
    const timeStamp = new Date().getTime()
    try {
        const response = yield call(api, `${getStoreAddressUrl}?timeStamp=${timeStamp}`, 'GET')
        yield put(getStoreAddressSuccess(response.recent_store.store_id))
        if (response) {
            yield put(getHomePageCategoryList())
            yield put(getHomePageData())
        }
    } catch (error) {
        yield put(getStoreAddressFailed())
    }
}

function* getCategoryFilters(action) {
    try {
        const { categoryId, storeId } = action.payload
        const payload = {
            category_id: categoryId,
        }
        if (storeId) {
            payload.store_id = storeId
        }
        const response = yield call(api, getFiltersURL, 'POST', payload, 0, null, false, language)
        if (response.filters.length === 0) {
            throw new Error('No filters available')
        }
        yield put(getCategoryFiltersSuccess(response))
    } catch (error) {
        yield put(filterErrors())
    }
}

function* getCateringFilters(action) {
    try {
        const { storeId, selectedPickupDate, selectedPickupTime } = action.payload
        const payload = {}
        if (storeId) {
            payload.store_id = storeId
        }
        if(selectedPickupDate && selectedPickupTime){
            payload.selectedPickupDate = selectedPickupDate,
            payload.selectedPickupTime = selectedPickupTime
        }
        const response = yield call(api, getCateringFiltersURL, 'POST', payload, 0, null, false, language)
        yield put(getCateringFiltersSuccess(response))
    } catch (error) {
        yield put(filterErrors())
    }
}

function* applyCategoryFilters(action) {
    try {
        const { location } = yield select()
        const storeLocation = location.toJS()
        const storeDetails = storeLocation.selectedStore
        const { categoryId, selectedSortTypes, selections } = action.payload
        const defaultURL = fetchSubCategories
        if (storeDetails) {
            const url = fetchSubCategories.replace('{storeId}', storeDetails.store_id.toString())
            const response = yield call(
                api,
                url,
                'POST',
                request.createApplyCategoryFiltersRequest(
                    storeDetails.store_id.toString(),
                    categoryId,
                    selectedSortTypes,
                    selections
                ),
                0,
                null,
                false,
                language,
                null,
                null,
                null,
                defaultURL
            )

            yield put(applyCategoryFiltersSuccess(queries.transformApplySubFilter(response)))
        } else {
            if (!storeDetails && selectedSortTypes && selectedSortTypes.length) {
                for (let i = 0; i < selectedSortTypes.length; i++) {
                    if (selectedSortTypes[i].toLowerCase() === 'price') {
                        selectedSortTypes[i] = 'catalog_price'
                    }
                }
            }
            const response = yield call(
                api,
                fetchGlobalCategoriesUrl,
                'POST',
                request.createApplyGlobalCategoryFiltersRequest(
                    categoryId,
                    selectedSortTypes,
                    selections
                ),
                0,
                null,
                false,
                language
            )

            yield put(applyCategoryFiltersSuccess(queries.transformApplySubFilter(response)))
        }
    } catch (error) {
        yield put(filterErrors())
    }
}

function* applyCateringFilters(action){
    try {
        const { location } = yield select()
        const storeLocation = location.toJS()
        const storeDetails = storeLocation.selectedStore
        const { categoryId, selectedSortTypes, selections, selectedPickupDate, selectedPickupTime } = action.payload
        const defaultURL = fetchCateringProducts
        const cateringData = getCateringData()
        const store =  cateringData?.store
        if (store,selectedPickupDate,selectedPickupTime) {
            const url = fetchCateringProducts.replace('{storeId}', store.store_id.toString())
            const response = yield call(
                api,
                url,
                'POST',
                request.createApplyCategoryFiltersRequest(
                    store.store_id.toString(),
                    categoryId,
                    selectedSortTypes,
                    selections,
                    selectedPickupDate,
                    selectedPickupTime
                ),
                0,
                null,
                false,
                language,
                null,
                null,
                null,
                defaultURL
            )
            yield put(applyCateringFiltersSuccess(queries.transformApplySubFilter(response)))            

        } else {
            if (!storeDetails && selectedSortTypes && selectedSortTypes.length) {
                for (let i = 0; i < selectedSortTypes.length; i++) {
                    if (selectedSortTypes[i].toLowerCase() === 'price') {
                        selectedSortTypes[i] = 'catalog_price'
                    }
                }
            }
            const response = yield call(
                api,
                fetchGlobalCateringProductsUrl,
                'POST',
                request.createApplyGlobalCategoryFiltersRequest(
                    categoryId,
                    selectedSortTypes,
                    selections
                ),
                0,
                null,
                false,
                language
            )

            yield put(applyCateringFiltersSuccess(queries.transformApplySubFilter(response)))
        }
    } catch (error) {
        yield put(filterErrors())
    }    
}
function* getSubCategoryFilters(action) {
    try {
        const response = yield call(
            api,
            getFiltersURL,
            'POST',
            action.payload,
            0,
            null,
            false,
            language
        )
        if (response.filters.length === 0) {
            throw new Error('No filters available')
        }
        yield put(getSubCategoryFiltersSuccess(response))
    } catch (error) {
        yield put(filterErrors())
    }
}

function* applySubCategoryFilters(action) {
    try {
        const { location } = yield select()
        const storeLocation = location.toJS()
        const storeDetails = storeLocation.selectedStore
        // const subCategoryFilters = products.get('subCategoryFilters')
        const { categoryId, selectedSortTypes, selections, subCategoryName } = action.payload
        let response
        const defaultURL = fetchSubCategories
        if (storeDetails != null) {
            const url = fetchSubCategories.replace('{storeId}', storeDetails.store_id.toString())
            response = yield call(
                api,
                url,
                'POST',
                request.createApplySubCategoriesFiltersRequest(
                    storeDetails.store_id.toString(),
                    categoryId,
                    subCategoryName,
                    selectedSortTypes,
                    selections
                ),
                0,
                null,
                false,
                language,
                null,
                null,
                null,
                defaultURL
            )
        } else {
            if (!storeDetails && selectedSortTypes && selectedSortTypes.length) {
                for (let i = 0; i < selectedSortTypes.length; i++) {
                    if (selectedSortTypes[i].toLowerCase() === 'price') {
                        selectedSortTypes[i] = 'catalog_price'
                    }
                }
            }
            response = yield call(
                api,
                fetchGlobalCategoriesUrl,
                'POST',
                request.createApplyGlobalSubCategoriesFiltersRequest(
                    categoryId,
                    subCategoryName,
                    selectedSortTypes,
                    selections
                ),
                0,
                null,
                false,
                language
            )
        }
        if (response) {
            const items = !isEmpty(response.subcategory)
                ? response.subcategory[0].items
                : response.items || response.subcategory
            const parsedItems = queries.parseSubcategoryProducts(items)
            if (parsedItems.length > 0) {
                yield put(applySubCategoryFiltersSuccess(parsedItems))
            } else {
                yield put(applySubCategoryFiltersSuccess([]))
            }
            yield put(
                setSelectedLocalizedSubCategory(
                    response.subcategory[0]?.localisedName || response.subcategory[0]?.name || ''
                )
            )
        }
    } catch (error) {
        yield put(filterErrors())
    }
}

function* getSubCateringFilters(action) {
    try {
        const response = yield call(
            api,
            getCateringFiltersURL,
            'POST',
            action.payload,
            0,
            null,
            false,
            language
        )
        yield put(getSubCateringFiltersSuccess(response))
    } catch (error) {
        yield put(filterErrors())
    }
}

function* applySubCateringFilters(action) {
    try {
        const { location } = yield select()
        const storeLocation = location.toJS()
        const storeDetails = storeLocation.selectedStore
        // const subCategoryFilters = products.get('subCategoryFilters')
        const { categoryId, selectedSortTypes, selections, subCategoryName, selectedPickupDate, selectedPickupTime } = action.payload
        let response
        const defaultURL = fetchCateringProducts
        const store = getCateringData()?.store
        if (store,selectedPickupDate,selectedPickupTime) {
            const url = fetchCateringProducts.replace('{storeId}', store.store_id.toString())
            response = yield call(
                api,
                url,
                'POST',
                request.createApplySubCategoriesFiltersRequest(
                    store.store_id.toString(),
                    categoryId,
                    subCategoryName,
                    selectedSortTypes,
                    selections,
                    selectedPickupDate,
                    selectedPickupTime
                ),
                0,
                null,
                false,
                language,
                null,
                null,
                null,
                defaultURL
            )
        } else {
            if (!storeDetails && selectedSortTypes && selectedSortTypes.length) {
                for (let i = 0; i < selectedSortTypes.length; i++) {
                    if (selectedSortTypes[i].toLowerCase() === 'price') {
                        selectedSortTypes[i] = 'catalog_price'
                    }
                }
            }
            response = yield call(
                api,
                fetchGlobalCateringProductsUrl,
                'POST',
                request.createApplyGlobalSubCategoriesFiltersRequest(
                    categoryId,
                    subCategoryName,
                    selectedSortTypes,
                    selections
                ),
                0,
                null,
                false,
                language
            )
        }
        if (response) {
            const items = !isEmpty(response.subcategory)
                ? response.subcategory[0].items
                : response.items || response.subcategory
            const parsedItems = queries.parseSubcategoryProducts(items)
            if (parsedItems.length > 0) {
                yield put(applySubCateringFiltersSuccess(parsedItems))
            } else {
                yield put(applySubCateringFiltersSuccess([]))
            }
            yield put(
                setSelectedLocalizedSubCategory(
                    response.subcategory[0]?.localisedName || response.subcategory[0]?.name || ''
                )
            )
        }
    } catch (error) {
        yield put(filterErrors())
    }
}

function* getProductDetails(action) {
    const { productId, storeId } = action.payload
    try {
        // TO DO: Check if the spinner is shown continously
        if (!productId || productId.length <= 0 || !storeId || storeId.length <= 0) return
        let response
        let url = productDetailsURL.replace('{id}', productId)
        url = url.replace('{storeId}', storeId.toString())
        const defaultURL = productDetailsURL
        response = yield call(
            api,
            url,
            'GET',
            1,
            0,
            null,
            false,
            language,
            null,
            null,
            null,
            defaultURL
        )

        let parse_getProductDetails = null

        if (response && response.Items && response.Items.length) {
            for (let i = 0; i < response.Items.length; i++) {
                const parsedProduct = util.parseProduct(response.Items[i])
                if (parsedProduct) parse_getProductDetails = parsedProduct
            }
        }
        if (parse_getProductDetails) {
            yield put(getProductDetailsSuccess(parse_getProductDetails))
        } else {
            yield put(getProductDetailsFailed())
        }
    } catch (error) {
        yield put(getProductDetailsFailed(error))
    }
}

function* getProductDetailsById(action) {
    const productId = action.payload

    try {
        if (!productId || productId.length <= 0) return
        const url = productDetailsByIdURL.replace('{id}', productId)
        const response = yield call(api, url, 'GET')
        yield put(getProductDetailsByIdSuccess(response.Items[0]))
    } catch (error) {
        yield put(getProductDetailsByIdFailed(error))
    }
}

function* searchProducts(action) {
    const request = action.payload
    try {
        const { location, catering } = yield select()
        
        // Use catering store when tab is catering, otherwise use shopping store
        const selectedStore = request.tab === 'catering' ? catering?.store : location.get('selectedStore')?.toJS()
        let url
        if (selectedStore !== null && selectedStore?.store_id) {
            url = storeProductSearchUrl.replace('{storeId}', selectedStore.store_id.toString())
        } else {
            url = productSearchUrl
        }
        const searchResults = yield call(api, url, 'POST', request, 2, null, false, language)

        const parse_searchResults = []
        if (searchResults && searchResults.Items && searchResults.Items.length) {
            searchResults.Items.forEach(item => {
                const parsedProduct = util.parseProduct(item)
                if (parsedProduct) parse_searchResults.push(parsedProduct)
            })
        }

        const final_parsed_searchResults = { Items: [] }
        final_parsed_searchResults.Items = parse_searchResults
        yield put(searchProductsSuccess(final_parsed_searchResults))
    } catch (error) {
        yield put(searchProductsFailed())
    }
}

function* loadProducts(action) {
    const request = action.payload
    try {
        const { location, catering } = yield select()
        
        // Use catering store when tab is catering, otherwise use shopping store
        const selectedStore = request.tab === 'catering' ? catering?.store : location.get('selectedStore')?.toJS()

        let url
        if (selectedStore !== null && selectedStore?.store_id) {
            url = storeProductSearchUrl.replace('{storeId}', selectedStore.store_id.toString())
        } else {
            url = productSearchUrl
        }
        const searchResults = yield call(api, url, 'POST', request, 2, null, false, language)
        const parse_searchResults = []
        if (searchResults && searchResults.Items && searchResults.Items.length) {
            searchResults.Items.forEach(item => {
                const parsedProduct = util.parseProduct(item)
                if (parsedProduct) parse_searchResults.push(parsedProduct)
            })
        }
        const final_parsed_searchResults = { Items: [] }
        final_parsed_searchResults.Items = parse_searchResults

        yield put(loadProductsSuccess(final_parsed_searchResults))
    } catch (error) {
        yield put(loadProductsFailed())
    }
}

function* getSearchedProduct(action) {
    const productId = action.payload

    try {
        const { location, catering } = yield select()
        
        // Get tab from URL query params (single source of truth), fallback to route check
        const urlParams = new URLSearchParams(window.location.search)
        const tabFromUrl = urlParams.get('tab')
        let tab = 'shop'
        if (tabFromUrl === 'catering' || tabFromUrl === 'shop') {
            tab = tabFromUrl
        } else if (window.location.pathname.startsWith('/catering')) {
            tab = 'catering'
        }
        
        const searchRequest = request.searchRequest(productId, tab)

        // Use catering store when tab is catering, otherwise use shopping store
        const selectedStore = tab === 'catering' ? catering?.store : location.get('selectedStore')?.toJS()

        let url
        if (selectedStore !== null && selectedStore?.store_id) {
            url = storeProductSearchUrl.replace('{storeId}', selectedStore.store_id.toString())
        } else {
            url = productSearchUrl
        }

        const productResult = yield call(api, url, 'POST', searchRequest, 0, null, false, language)

        if (productResult && productResult.Items && productResult.Items.length === 0) {
            yield put(getSearchedProductFailed())
            return
        }

        const product =
            productResult &&
            productResult.Items &&
            productResult.Items.length > 0 &&
            productResult.Items[0]

        const parsedProduct = util.parseProduct(product)
        if (parsedProduct) yield put(getSearchedProductSuccess(parsedProduct))
    } catch (error) {
        yield put(getSearchedProductFailed())
    }
}

function* getHomePageItemsWatcher() {
    yield takeLatest(actions.GET_HOME_PAGE_ITEMS, getHomePageItems)
}
function* getHomePageCategoryListWatcher() {
    yield takeLatest(actions.GET_HOME_PAGE_CATEGORIES_LIST, getHomePageCategories)
}

// function* getSubCategoriesWatcher() {
//     yield takeLatest(actions.GET_SUB_CATEGORIES, getSubCategories)
// }
function* getCategoryFiltersWatcher() {
    yield takeLatest(actions.GET_CATEGORY_FILTERS, getCategoryFilters)
}
function* getCateringFiltersWatcher() {
    yield takeLatest(actions.GET_CATERING_FILTERS, getCateringFilters)
}
function* applyCategoryFiltersWatcher() {
    yield takeLatest(actions.APPLY_CATEGORY_FILTERS, applyCategoryFilters)
}
function* applyCateringFiltersWatcher() {
    yield takeLatest(actions.APPLY_CATERING_FILTERS, applyCateringFilters)
}
function* getSubCategoriesFiltersWatcher() {
    yield takeLatest(actions.GET_SUB_CATEGORY_FILTERS, getSubCategoryFilters)
}
function* applySubCategoryFiltersWatcher() {
    yield takeLatest(actions.APPLY_SUB_CATEGORY_FILTERS, applySubCategoryFilters)
}
function* getSubCateringFiltersWatcher() {
    yield takeLatest(actions.GET_SUB_CATERING_FILTERS, getSubCateringFilters)
}
function* applySubCateringFiltersWatcher() {
    yield takeLatest(actions.APPLY_SUB_CATERING_FILTERS, applySubCateringFilters)
}
function* getProductDetailsWatcher() {
    yield takeLatest(actions.GET_PRODUCT_DETAILS, getProductDetails)
}
function* getProductDetailsByIdWatcher() {
    yield takeLatest(actions.GET_PRODUCT_DETAILS_BY_ID, getProductDetailsById)
}
function* searchProductsWatcher() {
    yield takeLatest(actions.SEARCH_PRODUCTS, searchProducts)
}
function* getSearchedProductWatcher() {
    yield takeLatest(actions.GET_SEARCHED_PRODUCT, getSearchedProduct)
}
function* loadProductsWatcher() {
    yield takeLatest(actions.LOAD_PRODUCTS, loadProducts)
}
function* getStoreAddressWatcher() {
    yield takeLatest(actions.GET_STORE_ADDRESS, getStoreAddress)
}
export default function* productsSaga() {
    yield all([
        getHomePageItemsWatcher(),
        // getSubCategoriesWatcher(),
        getCategoryFiltersWatcher(),
        getCateringFiltersWatcher(),
        applyCategoryFiltersWatcher(),
        applyCateringFiltersWatcher(),
        getSubCategoriesFiltersWatcher(),
        applySubCategoryFiltersWatcher(),
        getSubCateringFiltersWatcher(),
        getProductDetailsWatcher(),
        applySubCateringFiltersWatcher(),
        searchProductsWatcher(),
        getSearchedProductWatcher(),
        loadProductsWatcher(),
        getProductDetailsByIdWatcher(),
        getStoreAddressWatcher(),
        getHomePageCategoryListWatcher(),
    ])
}
